import React, { useState } from "react";
import axios from "axios";
import { 
  Play, 
  Youtube, 
  Download, 
  Calendar, 
  Trash2, 
  Clock, 
  Sparkles, 
  CheckCircle2, 
  AlertCircle,
  ExternalLink,
  RefreshCw
} from "lucide-react";
import { Button } from "./ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ShortCard = ({ short, onRefresh, onOpenPayPal }) => {
  const [publishing, setPublishing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [message, setMessage] = useState(null);

  const videoSrc = `${API}/shorts/${short.id}/video`;

  const handlePublish = async () => {
    setPublishing(true);
    setMessage(null);
    try {
      const token = localStorage.getItem("cortoviral_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.post(`${API}/shorts/${short.id}/publish`, {}, { headers, withCredentials: true });
      setMessage({ type: "success", text: "¡Publicado en YouTube Shorts con éxito!" });
      onRefresh();
    } catch (err) {
      if (err.response?.status === 402) {
        onOpenPayPal();
      } else {
        setMessage({ type: "error", text: err.response?.data?.detail || "Error al publicar." });
      }
    } finally {
      setPublishing(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("¿Seguro que deseas eliminar este Short?")) return;
    setDeleting(true);
    try {
      const token = localStorage.getItem("cortoviral_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/shorts/${short.id}`, { headers, withCredentials: true });
      onRefresh();
    } catch (err) {
      console.error(err);
    } finally {
      setDeleting(false);
    }
  };

  const getStatusBadge = () => {
    switch (short.status) {
      case "published":
        return <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> PUBLICADO</span>;
      case "ready":
        return <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-500/20 text-blue-400 border border-blue-500/30 flex items-center gap-1"><Sparkles className="w-3 h-3" /> LISTO</span>;
      case "scheduled":
        return <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-500/20 text-amber-400 border border-amber-500/30 flex items-center gap-1"><Clock className="w-3 h-3" /> PROGRAMADO</span>;
      default:
        return <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-slate-800 text-slate-300">{short.status}</span>;
    }
  };

  return (
    <div 
      data-testid={`short-card-${short.id}`}
      className="bg-[#12141D] border border-white/10 rounded-2xl overflow-hidden shadow-xl flex flex-col group hover:border-white/20 transition-all duration-200"
    >
      {/* Visual / Video Player 9:16 Preview */}
      <div className="relative aspect-[9/16] bg-black overflow-hidden flex items-center justify-center">
        {isPlaying ? (
          <video
            src={videoSrc}
            controls
            autoPlay
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full relative flex flex-col justify-between p-4 bg-gradient-to-b from-[#1E2235]/60 to-[#0A0B10]">
            <div className="flex items-center justify-between z-10">
              {getStatusBadge()}
              <span className="text-[11px] font-mono text-slate-400 bg-black/50 px-2 py-0.5 rounded">
                1080×1920
              </span>
            </div>

            {/* Hook text preview */}
            <div className="my-auto text-center px-3">
              <span className="text-[11px] uppercase tracking-wider text-red-400 font-bold block mb-1">
                Gancho Viral
              </span>
              <p className="text-sm font-semibold text-white line-clamp-3 italic">
                "{short.script?.hook || short.topic}"
              </p>
            </div>

            {/* Play Overlay */}
            <button
              data-testid={`play-video-btn-${short.id}`}
              onClick={() => setIsPlaying(true)}
              className="absolute inset-0 m-auto w-14 h-14 rounded-full bg-red-600/90 text-white flex items-center justify-center shadow-lg shadow-red-900/50 hover:scale-110 active:scale-95 transition-all z-20"
            >
              <Play className="w-6 h-6 ml-1 fill-white" />
            </button>

            <div className="z-10 flex items-center justify-between text-[11px] text-slate-400">
              <span>{short.script?.scenes?.length || 4} Escenas</span>
              <span>~15 seg</span>
            </div>
          </div>
        )}
      </div>

      {/* Info Card Footer */}
      <div className="p-4 flex flex-col flex-1 justify-between gap-3 bg-[#12141D]">
        <div>
          <h4 className="text-sm font-bold text-white line-clamp-2 title-font">
            {short.script?.title || short.topic}
          </h4>
          {short.engine_label && (
            <p className="text-[10px] text-slate-500 mt-1">Guion: {short.engine_label}</p>
          )}
          {short.ai_notice && (
            <p className="text-[10px] text-amber-400 mt-1 leading-snug">⚠ {short.ai_notice}</p>
          )}
          <p className="text-xs text-slate-400 mt-1 line-clamp-1 font-mono">
            {short.script?.seo?.tags?.map(t => `#${t}`).join(" ") || "#Shorts #Viral"}
          </p>
        </div>

        {message && (
          <div className={`p-2 rounded text-xs flex items-center gap-1.5 ${message.type === 'success' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
            {message.type === 'success' ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
            <span className="truncate">{message.text}</span>
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-2 pt-2 border-t border-white/5">
          {short.youtube_url ? (
            <div className="space-y-1">
              <a
                data-testid={`view-youtube-btn-${short.id}`}
                href={short.youtube_url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full bg-[#FF0033]/20 hover:bg-[#FF0033]/30 text-red-400 border border-red-500/30 font-bold py-2 rounded-lg text-xs flex items-center justify-center gap-1.5 transition-colors"
              >
                <Youtube className="w-4 h-4" /> Ver Enlace YouTube <ExternalLink className="w-3 h-3" />
              </a>
              {short.channel_published && (
                <span className="text-[10px] text-slate-400 block text-center">Publicado en: {short.channel_published}</span>
              )}
            </div>
          ) : (
            <Button
              data-testid={`publish-short-btn-${short.id}`}
              onClick={handlePublish}
              disabled={publishing || short.status !== "ready"}
              className="w-full bg-red-600 hover:bg-red-500 text-white font-bold py-2 rounded-lg text-xs shadow-md shadow-red-900/20 flex items-center justify-center gap-1.5"
            >
              {publishing ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Youtube className="w-3.5 h-3.5" />}
              Publicar a YouTube
            </Button>
          )}

          <div className="flex gap-2">
            <a
              data-testid={`download-video-btn-${short.id}`}
              href={videoSrc}
              download={`short_${short.id.slice(0, 8)}.mp4`}
              className="flex-1 bg-slate-800 hover:bg-slate-700 text-slate-200 py-1.5 rounded-lg text-xs font-semibold flex items-center justify-center gap-1 border border-white/10 transition-colors"
            >
              <Download className="w-3.5 h-3.5" /> MP4
            </a>
            <button
              data-testid={`delete-short-btn-${short.id}`}
              onClick={handleDelete}
              disabled={deleting}
              className="px-3 bg-red-950/30 hover:bg-red-900/40 text-red-400 py-1.5 rounded-lg text-xs border border-red-500/20 transition-colors"
              title="Eliminar short"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
